import { useId, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/shared/ui/cn'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/shared/ui/primitives/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/primitives/collapsible'

type CollapsibleCardProps = {
  title: ReactNode
  headerAction?: ReactNode
  summary?: ReactNode
  defaultOpen?: boolean
  children: ReactNode
}

function CollapsibleCard({ title, headerAction, summary, defaultOpen = false, children }: CollapsibleCardProps) {
  const [open, setOpen] = useState(defaultOpen)
  const titleId = useId()

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group">
      <Card className="gap-0">
        <CardHeader>
          <div className="relative flex w-full items-center gap-3">
            <CollapsibleTrigger
              aria-labelledby={titleId}
              className="absolute inset-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            />
            <span className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
              <CardTitle id={titleId} className="pb-0">
                {title}
              </CardTitle>
              {summary ? (
                <span
                  aria-hidden="true"
                  className="min-w-0 truncate text-sm font-normal text-muted-foreground group-data-[state=open]:hidden"
                >
                  {summary}
                </span>
              ) : null}
            </span>
            {headerAction ? (
              <CardAction onClick={(e) => e.stopPropagation()} className="relative shrink-0">
                {headerAction}
              </CardAction>
            ) : null}
            <ChevronDown
              aria-hidden="true"
              className="pointer-events-none size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180"
            />
          </div>
        </CardHeader>
        <CollapsibleContent
          forceMount
          className={cn(
            'grid transition-[grid-template-rows] duration-200 ease-out',
            'data-[state=closed]:grid-rows-[0fr] data-[state=open]:grid-rows-[1fr]',
          )}
        >
          <div className="min-h-0 overflow-hidden" aria-hidden={!open}>
            <CardContent className="pt-6">{children}</CardContent>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

export { CollapsibleCard }
export type { CollapsibleCardProps }
